import { FC, useMemo, useCallback, useState, useEffect } from 'react';
import { Loading, ReportCard, Section, Table } from '../../../../components';
import { useToastStore } from '../../../../stores';
import {
	getAllBuyers,
	TRevenda
} from '../../../../services';
import { Line } from 'react-chartjs-2';
import {
	FullLine,
	NoData,
	Separator,
	TableContainer,
	TableWrapper,
	TitleContainer,
} from '../../Reports.styles';

interface IGraphLineData {
	label: string;
	qtde: number;
}

const AdminShopkeeperReport: FC = () => {
	const { setToast } = useToastStore();
	const [loading, setLoading] = useState(false);
	const [buyers, setBuyers] = useState<TRevenda[]>([]);
	const [graphLineData, setGraphLineData] = useState<IGraphLineData[]>([]);
	const [clickGenerate, setClickGenerate] = useState(false);

	const fetchData = useCallback(async () => {
		setLoading(true);
		const data = await getAllBuyers() ;
		if (!data) {
			setToast({ variant: 'error', message: 'Erro ao buscar dados!' });
			setLoading(false);
			return;
		}

		const buyersCountByMonthYear: IGraphLineData[] = [];
		
		data.forEach((buyer) => {
			const dataCadastro = new Date(buyer.dataCadastro!);
			const label = `${dataCadastro.getFullYear()}/${dataCadastro.getMonth() + 1}`;
			const existingEntry = buyersCountByMonthYear.find((entry) => entry.label === label);

			if (existingEntry) {
				existingEntry.qtde++;
			} else {
				buyersCountByMonthYear.push({ label, qtde: 1 });
			}
		});

		buyersCountByMonthYear.sort((a, b) => {
			const [yearA, monthA] = a.label.split('/').map(Number);
			const [yearB, monthB] = b.label.split('/').map(Number);

			if (yearA !== yearB) {
				return yearA - yearB;
			}
			return monthA - monthB;
		});


		setGraphLineData(buyersCountByMonthYear);
		setBuyers(data);
		setClickGenerate(true);
		setLoading(false);
	}, [setToast, setLoading, setBuyers, setGraphLineData]);

	const tableColumns = useMemo(
		() => [

			{ id: 'registerDate', label: 'Registrado em', width: '100px' },
			{ id: 'fantasia', label: 'Nome' },
			{ id: 'numeroDocumento', label: 'CNPJ/CPF' },
			{ id: 'cidade', label: 'Cidade' },
			{ id: 'tagAtivo', label: 'Status', width: '100px' }
		],
		[]
	);

	const rowMapper = useCallback(
		(buyerData: TRevenda) => ({
			fantasia: {
				value: buyerData.fantasia,
				rawValue: buyerData.fantasia,
			},
			numeroDocumento: {
				value: buyerData.numeroDocumento,
				rawValue: buyerData.numeroDocumento,
			},
			cidade: {
				value: buyerData.cidade,
				rawValue: buyerData.cidade,
			},
			registerDate: {
				value: new Date(buyerData.dataCadastro!).toLocaleDateString('pt-BR'),
				rawValue: new Date(buyerData.dataCadastro!).getTime(),
			},
			tagAtivo: {
				value: (buyerData.tagAtivo === '1' ? 'Ativo' : 'Inativo'),
				rawValue: buyerData.tagAtivo,
			}
		}),
		[]
	);

	const MemoizedTable = useMemo(
		() => <Table columns={tableColumns} rows={buyers?.map(rowMapper)} paginate exportToPDF exportToCSV />,
		[buyers, rowMapper, tableColumns]
	);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	return (
		<Section>
			<TitleContainer>Relatório de Revendas</TitleContainer>
			<Separator />
			{loading && <Loading />}
			{buyers?.length > 0 && (
				<ReportCard
					data={[
						{
							color: '#FF9800',
							title: 'Qtde Total de Revendas',
							value: buyers.length,
						},
						{
							color: '#4CAF50',
							title: 'Qtde Revendas Ativas',
							value: buyers.filter((buyer) => buyer.tagAtivo === '1').length,
						},
						{
							color: '#f44336',
							title: 'Qtde Revendas Inativas',
							value: buyers.filter((buyer) => buyer.tagAtivo === '0').length,
						}
					]}
				/>
			)}
			<TableContainer>
				{(loading || (buyers?.length || 0) > 0) && (
					<TableWrapper>{loading ?  <Loading /> : MemoizedTable}</TableWrapper>
				)}
				{!loading && clickGenerate && buyers?.length === 0 && (
					<NoData>Nenhum registro encontrado</NoData>
				)}
				{graphLineData.length > 0 && (
					<FullLine>
						<Line
							data={{
								labels: graphLineData.map((item) => item.label),
								datasets: [
									{
										label: 'Cadastros por período',
										data: graphLineData.map((item) => item.qtde),
										backgroundColor: 'rgba(255, 102, 0, 0.5)',
										borderColor: 'rgba(255, 102, 0)',
									},
								],
							}}
						/>
					</FullLine>
				)}
			</TableContainer>
			
		</Section>
	);
};

export default AdminShopkeeperReport;
